import React, { useState } from "react";
import { auth, ErroApi } from "./api";
import {
  User, Lock, Eye, EyeOff, KeyRound, Loader2, AlertTriangle, ShieldCheck,
  Building2, Ticket, UserPlus, ArrowLeft, CheckCircle2, Info,
} from "lucide-react";
import { LogoEixo } from "./ui/marca";

/**
 * LOGIN REAL
 *
 * O do protótipo aceitava qualquer senha e escolhia o usuário de uma lista.
 * Este chama a API, trata segundo fator e bloqueio por tentativa.
 */
export function Login({ aoEntrar }: { aoEntrar: (s: any) => void }) {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [codigo, setCodigo] = useState("");
  const [pedeMfa, setPedeMfa] = useState(false);
  const [ver, setVer] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [espera, setEspera] = useState(0);

  /** "entrar" | "cadastrar". O MFA continua sendo um passo dentro de entrar. */
  const [modo, setModo] = useState<"entrar" | "cadastrar">("entrar");

  /**
   * Só oferece cadastro se o servidor disser que aceita.
   *
   * Começa escondido de propósito: enquanto a resposta não chega, ou se o
   * servidor for uma versão anterior à rota, o botão não aparece. Botão que
   * erra ao ser clicado é pior que botão ausente.
   */
  const [temCadastro, setTemCadastro] = useState(false);
  React.useEffect(() => {
    let vivo = true;
    auth.cadastroDisponivel().then((v) => { if (vivo) setTemCadastro(v); });
    return () => { vivo = false; };
  }, []);
  const [ok, setOk] = useState("");

  /**
   * Chegou aqui vindo do endereço antigo (ver endereco.ts).
   *
   * O login não acompanha a mudança — a sessão é do endereço — e o ícone
   * instalado no celular continua apontando para o antigo. Sem este aviso, a
   * oficina vê a tela de login do nada e liga achando que caiu.
   *
   * O `?mudou=1` sai da barra logo em seguida: não pode ir parar no ícone
   * que a pessoa vai instalar, senão o aviso apareceria em toda abertura.
   */
  const [mudouDeEndereco] = useState(() => new URLSearchParams(location.search).has("mudou"));
  React.useEffect(() => {
    if (mudouDeEndereco) history.replaceState(null, "", location.pathname);
  }, [mudouDeEndereco]);

  const vazio = {
    codigo: "", razaoSocial: "", cnpj: "", municipio: "",
    uf: "", ibge: "", nome: "", email: "", senha: "",
  };
  const [novo, setNovo] = useState(vazio);
  const campo = (k: keyof typeof vazio) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setNovo({ ...novo, [k]: e.target.value });

  /**
   * Cria a oficina e volta para o login com o e-mail preenchido.
   *
   * Não entra direto de propósito: a sessão nasce pelo caminho normal, que é
   * o único que passa por bloqueio de tentativa, MFA e rotação de refresh.
   */
  async function cadastrar() {
    setErro(""); setOk(""); setCarregando(true);
    try {
      const r = await auth.criarOficina(novo);
      setEmail(r.email);
      setNovo(vazio);
      setModo("entrar");
      setOk("Oficina criada. Entre com o e-mail e a senha que você acabou de definir.");
    } catch (e: any) {
      setErro(e?.message ?? "Não consegui conectar ao servidor.");
    } finally {
      setCarregando(false);
    }
  }

  async function submeter() {
    if (!email.trim() || !senha) { setErro("Preencha e-mail e senha."); return; }
    // Limpa o aviso de "oficina criada" junto: senão ele fica ao lado do erro
    // e a tela mostra sucesso e falha ao mesmo tempo.
    setErro(""); setOk(""); setCarregando(true);
    try {
      const s = await auth.entrar(email.trim(), senha, codigo || undefined);
      aoEntrar(s);
    } catch (e: any) {
      if (e instanceof ErroApi && e.codigo === "mfa_requerido") {
        // Não é erro: é o passo seguinte do fluxo.
        setPedeMfa(true); setErro("");
      } else if (e instanceof ErroApi && e.codigo === "muitas_tentativas") {
        setEspera(e.esperarSegundos ?? 60);
        setErro(e.message);
      } else {
        setErro(e?.message ?? "Não consegui conectar ao servidor.");
      }
    } finally {
      setCarregando(false);
    }
  }

  return (
    <div className="of grid-bg min-h-screen flex items-center justify-center p-5" style={{ background: "var(--ink)" }}>
      <div className="w-full grid gap-8 justify-items-center" style={{ maxWidth: 420 }}>
        <div className="flex justify-center mb-1">
          <LogoEixo tamanho={56} />
        </div>

        <div className="card w-full rise" style={{ overflow: "hidden" }}>
          <div className="stripe" />
          <div className="px-6 pt-5 pb-6">
            <h1 className="dsp" style={{ fontSize: 20 }}>
              {modo === "cadastrar" ? "Criar oficina" : pedeMfa ? "Segundo fator" : "Entrar"}
            </h1>
            <p className="text-sm t-mut mt-1 mb-5">
              {modo === "cadastrar"
                ? "Estes dados vão nas notas fiscais. Confira antes de salvar."
                : pedeMfa
                ? "Informe o código do aplicativo autenticador."
                : "Use o e-mail e a senha da sua conta."}
            </p>

            {modo === "cadastrar" ? (
              <>
                <div className="mb-3.5">
                  <label className="lbl">Código de convite</label>
                  <div className="relative">
                    <Ticket size={15} className="absolute t-mut2" style={{ left: 10, top: "50%", transform: "translateY(-50%)" }} />
                    <input className="inp inp-mono" style={{ paddingLeft: 32 }} value={novo.codigo}
                      onChange={campo("codigo")} autoComplete="off" />
                  </div>
                </div>

                <div className="mb-3.5">
                  <label className="lbl">Razão social</label>
                  <div className="relative">
                    <Building2 size={15} className="absolute t-mut2" style={{ left: 10, top: "50%", transform: "translateY(-50%)" }} />
                    <input className="inp" style={{ paddingLeft: 32 }} value={novo.razaoSocial}
                      onChange={campo("razaoSocial")} />
                  </div>
                </div>

                <div className="mb-3.5">
                  <label className="lbl">CNPJ</label>
                  <input className="inp inp-mono" value={novo.cnpj} inputMode="numeric"
                    placeholder="00.000.000/0000-00" onChange={campo("cnpj")} />
                </div>

                <div className="grid gap-3 mb-3.5" style={{ gridTemplateColumns: "1fr 88px" }}>
                  <div>
                    <label className="lbl">Município</label>
                    <input className="inp" value={novo.municipio} onChange={campo("municipio")} />
                  </div>
                  <div>
                    <label className="lbl">UF</label>
                    <input className="inp" value={novo.uf} maxLength={2}
                      style={{ textTransform: "uppercase" }} onChange={campo("uf")} />
                  </div>
                </div>

                <div className="mb-3.5">
                  <label className="lbl">Código IBGE do município</label>
                  <input className="inp inp-mono" value={novo.ibge} inputMode="numeric"
                    maxLength={7} placeholder="7 dígitos" onChange={campo("ibge")} />
                  {/* É ele que define para qual prefeitura a NFS-e vai. Errar
                      aqui é emitir nota no município errado. */}
                  <div className="text-xs t-mut2 mt-1.5">
                    Define para qual prefeitura a nota de serviço vai. Confira no site do IBGE.
                  </div>
                </div>

                <div className="mb-3.5">
                  <label className="lbl">Nome do proprietário</label>
                  <div className="relative">
                    <User size={15} className="absolute t-mut2" style={{ left: 10, top: "50%", transform: "translateY(-50%)" }} />
                    <input className="inp" style={{ paddingLeft: 32 }} value={novo.nome} onChange={campo("nome")} />
                  </div>
                </div>

                <div className="mb-3.5">
                  <label className="lbl">E-mail (será o login)</label>
                  <input className="inp" value={novo.email} autoComplete="email"
                    inputMode="email" onChange={campo("email")} />
                </div>

                <div className="mb-4">
                  <label className="lbl">Senha</label>
                  <div className="relative">
                    <Lock size={15} className="absolute t-mut2" style={{ left: 10, top: "50%", transform: "translateY(-50%)" }} />
                    <input className="inp" style={{ paddingLeft: 32, paddingRight: 38 }}
                      type={ver ? "text" : "password"} value={novo.senha} autoComplete="new-password"
                      onChange={campo("senha")} onKeyDown={(e) => e.key === "Enter" && cadastrar()} />
                    <button onClick={() => setVer(!ver)} className="absolute btn btn-g btn-xs"
                      style={{ right: 4, top: "50%", transform: "translateY(-50%)" }}
                      aria-label={ver ? "Ocultar senha" : "Mostrar senha"}>
                      {ver ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  <div className="text-xs t-mut2 mt-1.5">
                    Mínimo 10 caracteres. Uma frase curta que só você diria funciona bem.
                  </div>
                </div>
              </>
            ) : !pedeMfa ? (
              <>
                <div className="mb-3.5">
                  <label className="lbl">E-mail</label>
                  <div className="relative">
                    <User size={15} className="absolute t-mut2" style={{ left: 10, top: "50%", transform: "translateY(-50%)" }} />
                    <input className="inp" style={{ paddingLeft: 32 }} value={email} autoComplete="username"
                      onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submeter()} />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="lbl">Senha</label>
                  <div className="relative">
                    <Lock size={15} className="absolute t-mut2" style={{ left: 10, top: "50%", transform: "translateY(-50%)" }} />
                    <input className="inp" style={{ paddingLeft: 32, paddingRight: 38 }} type={ver ? "text" : "password"}
                      value={senha} autoComplete="current-password"
                      onChange={(e) => setSenha(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submeter()} />
                    <button onClick={() => setVer(!ver)} className="absolute btn btn-g btn-xs"
                      style={{ right: 4, top: "50%", transform: "translateY(-50%)" }}
                      aria-label={ver ? "Ocultar senha" : "Mostrar senha"}>
                      {ver ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="mb-4">
                <label className="lbl">Código de 6 dígitos</label>
                <div className="relative">
                  <ShieldCheck size={15} className="absolute t-mut2" style={{ left: 10, top: "50%", transform: "translateY(-50%)" }} />
                  <input className="inp inp-mono" style={{ paddingLeft: 32, fontSize: 18, letterSpacing: ".2em" }}
                    value={codigo} inputMode="numeric" maxLength={11} autoFocus
                    onChange={(e) => setCodigo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submeter()} />
                </div>
                <div className="text-xs t-mut2 mt-2">
                  Perdeu o aparelho? Use um dos códigos de recuperação que você guardou.
                </div>
              </div>
            )}

            {mudouDeEndereco && modo === "entrar" && (
              <div className="flex items-start gap-2 mb-3.5 px-3 py-2.5 text-sm"
                style={{ background: "var(--gulf-l)", border: "1px solid #BEE0EC", borderRadius: "var(--r)", color: "var(--gulf-d)" }}>
                <Info size={15} className="mt-0.5 flex-none" />
                <span>
                  O sistema agora se chama <b>Eixo Auto Control</b> e fica em <b>eixoautocontrol.com.br</b>.
                  Entre com o mesmo e-mail e senha. No celular, adicione o ícone de novo a partir deste endereço.
                </span>
              </div>
            )}

            {ok && (
              <div className="flex items-start gap-2 mb-3.5 px-3 py-2.5 text-sm"
                style={{ background: "var(--ok-l, #E6F4EA)", border: "1px solid #B7DFC2", borderRadius: "var(--r)", color: "var(--ok, #2F6B3D)" }}>
                <CheckCircle2 size={15} className="mt-0.5 flex-none" />{ok}
              </div>
            )}

            {erro && (
              <div className="flex items-start gap-2 mb-3.5 px-3 py-2.5 text-sm"
                style={{ background: "var(--crit-l)", border: "1px solid #F1C9C5", borderRadius: "var(--r)", color: "var(--crit)" }}>
                <AlertTriangle size={15} className="mt-0.5 flex-none" />{erro}
              </div>
            )}

            {modo === "cadastrar" ? (
              <>
                <button className="btn btn-p btn-lg w-full" onClick={cadastrar} disabled={carregando}>
                  {carregando ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />}
                  {carregando ? "Criando…" : "Criar oficina"}
                </button>
                <button className="btn btn-g w-full mt-2"
                  onClick={() => { setModo("entrar"); setErro(""); }}>
                  <ArrowLeft size={15} /> Já tenho conta
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-p btn-lg w-full" onClick={submeter} disabled={carregando || espera > 0}>
                  {carregando ? <Loader2 size={15} className="animate-spin" /> : <KeyRound size={15} />}
                  {carregando ? "Verificando…" : pedeMfa ? "Confirmar" : "Entrar"}
                </button>

                {pedeMfa ? (
                  <button className="btn btn-g w-full mt-2" onClick={() => { setPedeMfa(false); setCodigo(""); }}>
                    Voltar
                  </button>
                ) : temCadastro ? (
                  <button className="btn btn-g w-full mt-2"
                    onClick={() => { setModo("cadastrar"); setErro(""); setOk(""); }}>
                    <UserPlus size={15} /> Criar oficina
                  </button>
                ) : null}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
